import { Injectable, inject } from '@angular/core';
import { Platform } from '@ionic/angular';
import { KeepAwake } from '@capacitor-community/keep-awake';
import { DebugLoggerService } from './debug-logger.service';

declare var window: any;

@Injectable({ providedIn: 'root' })
export class AudioWakeLockService {
  private debugLoggerService = inject(DebugLoggerService);
  private platform = inject(Platform);

  private isWakeLockActive = false;
  private isBackgroundModeActive = false;

  async activateForAudio(): Promise<void> {
    await this.activateKeepAwake();
    this.activateBackgroundMode();
  }

  async deactivateForAudio(): Promise<void> {
    await this.deactivateKeepAwake();
    this.deactivateBackgroundMode();
  }

  private async activateKeepAwake(): Promise<void> {
    if (this.isWakeLockActive) return;
    try {
      const { isSupported } = await KeepAwake.isSupported();
      if (!isSupported) {
        this.debugLoggerService.log('AudioWakeLock', 'KeepAwake not supported on this platform');
        return;
      }
      await KeepAwake.keepAwake();
      this.isWakeLockActive = true;
      this.debugLoggerService.log('AudioWakeLock', 'KeepAwake activated — screen will stay on while audio is active');
    } catch (error) {
      this.debugLoggerService.error('AudioWakeLock', 'Failed to activate KeepAwake:', error);
    }
  }

  private async deactivateKeepAwake(): Promise<void> {
    if (!this.isWakeLockActive) return;
    try {
      await KeepAwake.allowSleep();
      this.isWakeLockActive = false;
      this.debugLoggerService.log('AudioWakeLock', 'KeepAwake deactivated — screen can dim/sleep again');
    } catch (error) {
      this.debugLoggerService.error('AudioWakeLock', 'Failed to deactivate KeepAwake:', error);
    }
  }

  private activateBackgroundMode(): void {
    if (this.isBackgroundModeActive) return;
    if (!this.platform.is('ios')) return;

    try {
      const bgMode = window?.cordova?.plugins?.backgroundMode;
      if (!bgMode) {
        this.debugLoggerService.log('AudioWakeLock', 'cordova-plugin-background-mode not available');
        return;
      }

      bgMode.setDefaults({ title: 'Aforo Mesh', text: 'Audio streaming is running in the background' });
      bgMode.enable();

      this.isBackgroundModeActive = true;
      this.debugLoggerService.log('AudioWakeLock', 'Background mode enabled for audio streaming (iOS)');
    } catch (error) {
      this.debugLoggerService.error('AudioWakeLock', 'Failed to activate background mode:', error);
    }
  }

  private deactivateBackgroundMode(): void {
    if (!this.isBackgroundModeActive) return;
    try {
      const bgMode = window?.cordova?.plugins?.backgroundMode;
      if (bgMode) {
        bgMode.disable();
      }
      this.isBackgroundModeActive = false;
      this.debugLoggerService.log('AudioWakeLock', 'Background mode disabled (iOS)');
    } catch (error) {
      this.debugLoggerService.error('AudioWakeLock', 'Failed to deactivate background mode:', error);
    }
  }

  isActive(): boolean {
    return this.isWakeLockActive || this.isBackgroundModeActive;
  }
}
